import { useState } from "react";

import { faCheck, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useTimeout } from "usehooks-ts";

import { Button } from "src/base-components/Button";
import { ToolUse } from "src/clients/copilot";

type Props = {
  toolUse: ToolUse;
  onApprove: () => void;
  onDeny: () => void;
  disabled?: boolean;
  delay?: number;
};

type ToolInput = {
  schema_name?: string;
  table_name?: string;
  tables?: { table_name: string }[];
};

const getToolDescription = (
  toolName: string,
  toolInput: ToolInput,
): React.ReactNode => {
  switch (toolName) {
    case "get_tables":
      return "Allow Copilot to fetch the list of all available tables";
    case "get_table_details":
      return (
        <span>
          Allow Copilot to retrieve column details for the{" "}
          <span className="font-code-12">
            {toolInput.schema_name}.{toolInput.table_name}
          </span>{" "}
          table
        </span>
      );
    case "get_batch_table_details":
      return (
        <span>
          Allow Copilot to fetch details for the following tables:
          <ul className="list-inside list-disc">
            {toolInput.tables?.map((table: { table_name: string }) => (
              <li key={table.table_name} className="rounded-md font-code-12">
                {table.table_name}
              </li>
            ))}
          </ul>
        </span>
      );

    default:
      return `Allow Copilot to use ${toolName}`;
  }
};

export const ToolApprovalCard: React.FC<Props> = ({
  toolUse,
  onApprove,
  onDeny,
  disabled = false,
  delay = 0,
}) => {
  const description = getToolDescription(toolUse.tool_name, toolUse.tool_input);
  const [render, setRender] = useState(delay === 0);

  useTimeout(() => setRender(true), delay > 0 ? 1000 * delay : null);

  if (!render) {
    return null;
  }

  return (
    <div>
      <p className="pb-2 font-inter-normal-12px text-gray-800">{description}</p>

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-2">
          <Button
            disabled={disabled}
            iconLeft={faCheck}
            size="sm"
            variant="secondary"
            onClick={onApprove}
          >
            Yes, allow
          </Button>
          <Button
            disabled={disabled}
            iconLeft={faTimes}
            size="sm"
            variant="secondary"
            onClick={onDeny}
          >
            Don't allow
          </Button>
        </div>
      </div>
    </div>
  );
};
